import { SiteChangeLog } from "@inrange/building-manager-api-client/models-site";
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import stringToReactNodesWithLinks from "./stringToReactNodesWithLinks";

export const ARRAY_DEDUP_FIELDS = new Set([
  "buildings",
  "siteOwnerships",
  "sdmMatches",
  "siteFiles",
]);

export const timeFormats = {
  full: {
    day: "numeric",
    year: "numeric",
    month: "short",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZone: "UTC",
    timeZoneName: "short",
  } as const,
  date: {
    day: "numeric",
    year: "numeric",
    month: "short",
  } as const,
};

export const timeDetails: Record<string, keyof typeof timeFormats> = {
  hubspotUpdateTime: "full",
  pvDesignSystemLastUpdatedAt: "full",
  commerciallyOperationalDateEpochSeconds: "date",
  modellingFrom: "date",
  leaseLengthDateEpochSeconds: "date",
  "siteFiles.date": "date",
  "siteFiles.invoiceDueDate": "date",
  "siteFiles.issueDate": "date",
  updatedAt: "date",
};

const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

export const timeFromEpochString = (time: string | number, value = "") => {
  if (testMode) {
    return "(fixed-time-for-testing)";
  }
  const intTime = Number(time);
  const dateTime = DateTime.fromSeconds(intTime)
    .toUTC()
    .toLocaleString(timeFormats[timeDetails[value]] || timeFormats.full);
  return dateTime;
};

// System actors (e.g. survey sync) write SiteChangeLog rows with non-email
// updatedBy values that we still want shown verbatim in the User column,
// rather than bucketed as a migration.
const isSystemActor = (updatedBy: string) => updatedBy.startsWith("Survey:");

export const getUpdatedBy = (logItem: SiteChangeLog) => {
  if (
    !logItem.updatedBy.includes("@") &&
    !isSystemActor(logItem.updatedBy) &&
    !(
      Object.values(logItem.userChange).length == 1 &&
      logItem.userChange["investmentModel"]
    )
  ) {
    return "migrations@inrange.io";
  }
  return logItem.updatedBy;
};

export const getUserNote = (logItem: SiteChangeLog) => {
  let userNote = logItem.userNote;
  if (
    !userNote &&
    !logItem.updatedBy.includes("@") &&
    !isSystemActor(logItem.updatedBy)
  ) {
    userNote = logItem.updatedBy;
  }
  if (userNote) {
    return <span>{stringToReactNodesWithLinks(userNote)}</span>;
  }
  return undefined;
};

const isEmptyValue = (value: any): boolean =>
  value === null || value === undefined || value === "";

const NESTED_HIDDEN_KEYS = new Set([
  "siteID",
  "isTest",
  "isTemporary",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
  "createdByName",
  "updatedByName",
  "updatedByEmail",
]);

const filterForDisplay = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) => !NESTED_HIDDEN_KEYS.has(key) && !isEmptyValue(value)
    )
  );

const formatArrayField = (
  lossFactorsByKey: Record<string, any>,
  siteId: string,
  formatFieldName: (
    lossFactorsByKey: Record<string, any>,
    fieldName: string
  ) => string,
  recordLabeller: (record: any) => string,
  idFields: string[],
  hideFields: Set<string>,
  fieldNamePrefix: string,
  detail: any[]
) => {
  if (detail.length === 0) {
    return "";
  }

  const renderSubFields = (match: any) => {
    if (testMode) {
      for (const idField of idFields) {
        if (match[idField]) {
          match[idField] = "fixed-id-for-testing";
        }
      }
    }
    return Object.entries(match)
      .sort(([a, _], [b, __]) => {
        const aName = formatFieldName(
          lossFactorsByKey,
          `${fieldNamePrefix}.${a}`
        );
        const bName = formatFieldName(
          lossFactorsByKey,
          `${fieldNamePrefix}.${b}`
        );
        return aName.localeCompare(bName);
      })
      .filter(([key, _]) => !hideFields.has(key))
      .filter(([, value]) => !isEmptyValue(value))
      .map(([key, value]) => (
        <li key={key}>
          {formatFieldName(lossFactorsByKey, `${fieldNamePrefix}.${key}`)}:{" "}
          {formatDetail(
            lossFactorsByKey,
            siteId,
            formatFieldName,
            `${fieldNamePrefix}.${key}`,
            value
          )}
        </li>
      ));
  };

  if (detail.length === 1) {
    const subFields = renderSubFields(detail[0]);
    return (
      <span>
        {recordLabeller(detail[0])}
        {subFields.length > 0 && <ul>{subFields}</ul>}
      </span>
    );
  }

  return (
    <ul>
      {detail.map((match, index) => {
        return (
          <li key={index}>
            {recordLabeller(match)}
            <ul>{renderSubFields(match)}</ul>
          </li>
        );
      })}
    </ul>
  );
};

export const formatDetail = (
  lossFactorsByKey: Record<string, any>,
  siteId: string,
  formatFieldName: (
    lossFactorsByKey: Record<string, any>,
    fieldName: string
  ) => string,
  fieldName: string,
  detail: any
): string | JSX.Element => {
  const lastFieldNamePart = fieldName.split(".").slice(-1)[0];
  if (typeof detail === "boolean") {
    return String(detail);
  } else if (fieldName in timeDetails) {
    return detail ? timeFromEpochString(detail, fieldName) : "(no value)";
  } else if (lastFieldNamePart in timeDetails) {
    return detail
      ? timeFromEpochString(detail, lastFieldNamePart)
      : "(no value)";
  } else if (Array.isArray(detail)) {
    if (fieldName === "sdmMatches") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        (match) => (match["buyerId"] === siteId ? "Buy match" : "Sell match"),
        ["buyerId", "sellerId", "buyerEsysUuid", "sellerEsysUuid"],
        new Set([
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "sdmMatches",
        detail
      );
    } else if (fieldName === "siteOwnerships") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        (match) => {
          const label = match.name
            ? `${match.name} (${match.ownership})`
            : "Ownership";
          return match.isTest ? `${label} - test org` : label;
        },
        ["orgID"],
        new Set([
          "siteID",
          "isTemporary",
          "orgID",
          "name",
          "ownership",
          "isTest",
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "siteOwnerships",
        detail
      );
    } else if (fieldName === "buildings") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        () => "Building",
        ["orgID"],
        new Set([
          "siteID",
          "id",
          "sourceGeometry",
          "polygonType",
          "sourcePolygonType",
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "buildings",
        detail
      );
    } else if (fieldName === "siteFiles") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        () => "File",
        ["orgID"],
        new Set([
          "siteID",
          "fileGuid",
          "fileVersionId",
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "siteFiles",
        detail
      );
    } else if (fieldName === "tariffConfig.supplier.customFixedTariffs") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        () => "Custom Fixed Tariff",
        [""],
        new Set([
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "cft",
        detail
      );
    } else if (fieldName === "tariffConfig.supplier.customStandingCharges") {
      return formatArrayField(
        lossFactorsByKey,
        siteId,
        formatFieldName,
        () => "Custom Standing Charge",
        [""],
        new Set([
          // New base fields
          "createdBy",
          "createdAt",
          "updatedBy",
          "updatedAt",
          // Old base fields
          "createdByName",
          "updatedByName",
          "updatedByEmail",
        ]),
        "cst",
        detail
      );
    } else {
      if (typeof detail[0] === "object") {
        return (
          <ol>
            {detail.map((item, index) => {
              return (
                <li key={index}>
                  {JSON.stringify(
                    filterForDisplay(item),
                    undefined,
                    1
                  ).replaceAll('"', "")}
                </li>
              );
            })}
          </ol>
        );
      }
      return `[${detail.join(", ")}]`;
    }
  }
  if (fieldName === "notes" && typeof detail === "string") {
    return <span>{stringToReactNodesWithLinks(detail)}</span>;
  }
  if (detail !== null && typeof detail === "object") {
    return (
      JSON.stringify(filterForDisplay(detail), undefined, 1) || "(no value)"
    );
  }
  return !!detail || detail === 0 ? detail : "(no value)";
};

export const removeUnchangedArrayItems = (detail: {
  before: any[];
  after: any[];
}) => {
  const beforeArray = detail.before || [];
  const afterArray = detail.after || [];
  // This implementation is O(n^2) but the arrays are expected to be small
  // A better implementation would hash the values and compare the hashes
  // but this would be slightly more complex code and not worth it for small arrays
  const commonItems = beforeArray.filter((item) =>
    afterArray.some((otherItem) => isEqual(item, otherItem))
  );
  detail.before = beforeArray.filter(
    (item) => !commonItems.some((otherItem) => isEqual(item, otherItem))
  );
  detail.after = afterArray.filter(
    (item) => !commonItems.some((otherItem) => isEqual(item, otherItem))
  );
};

const ARRAY_FIELD_MATCH_CONFIGS: Record<
  string,
  {
    matchKeyFn: (item: any) => string;
    retainKeys: string[];
    summarizeFields?: Set<string>;
    fallbackMatchByIndex?: boolean;
  }
> = {
  siteFiles: {
    matchKeyFn: (item) => item.fileName || "",
    retainKeys: ["fileName"],
  },
  siteOwnerships: {
    matchKeyFn: (item) => `${item.orgID || ""}-${item.ownership || ""}`,
    retainKeys: ["orgID", "ownership"],
  },
  buildings: {
    matchKeyFn: (item) => item.id || "",
    retainKeys: ["name"],
    summarizeFields: new Set(["geometry"]),
    fallbackMatchByIndex: true,
  },
  sdmMatches: {
    matchKeyFn: (item) => `${item.buyerId || ""}-${item.sellerId || ""}`,
    retainKeys: ["buyerId", "sellerId"],
  },
};

const filterEmptyValues = (item: any): any => {
  const filtered: any = {};
  for (const [key, value] of Object.entries(item)) {
    if (!isEmptyValue(value)) {
      filtered[key] = value;
    }
  }
  return filtered;
};

export const filterArrayFieldForDiff = (
  fieldName: string,
  detail: { before: any; after: any }
): { before: any; after: any } => {
  const config = ARRAY_FIELD_MATCH_CONFIGS[fieldName];
  if (!config) return detail;

  const { matchKeyFn, retainKeys, summarizeFields, fallbackMatchByIndex } =
    config;
  const beforeArray: any[] = detail.before || [];
  const afterArray: any[] = detail.after || [];
  const filteredBefore: any[] = [];
  const filteredAfter: any[] = [];
  const usedBeforeIndices = new Set<number>();
  const usedAfterIndices = new Set<number>();

  const pushMatchedPair = (beforeItem: any, afterItem: any) => {
    const filteredBeforeItem: any = {};
    const filteredAfterItem: any = {};

    for (const key of retainKeys) {
      if (key in beforeItem) filteredBeforeItem[key] = beforeItem[key];
      if (key in afterItem) filteredAfterItem[key] = afterItem[key];
    }

    const allKeys = new Set([
      ...Object.keys(beforeItem),
      ...Object.keys(afterItem),
    ]);
    for (const key of allKeys) {
      if (!isEqual(beforeItem[key], afterItem[key])) {
        if (summarizeFields?.has(key)) {
          filteredBeforeItem[key] = "(original)";
          filteredAfterItem[key] = "(changed)";
        } else {
          filteredBeforeItem[key] = beforeItem[key];
          filteredAfterItem[key] = afterItem[key];
        }
      }
    }

    filteredBefore.push(filteredBeforeItem);
    filteredAfter.push(filteredAfterItem);
  };

  // First pass: match by key
  for (let bi = 0; bi < beforeArray.length; bi++) {
    const beforeItem = beforeArray[bi];
    const matchKey = matchKeyFn(beforeItem);
    const afterIndex = afterArray.findIndex(
      (item, idx) =>
        !usedAfterIndices.has(idx) && matchKeyFn(item) === matchKey
    );

    if (afterIndex >= 0) {
      usedBeforeIndices.add(bi);
      usedAfterIndices.add(afterIndex);
      pushMatchedPair(beforeItem, afterArray[afterIndex]);
    }
  }

  // Second pass: match remaining items by index within unmatched arrays
  if (fallbackMatchByIndex) {
    const unmatchedBefore = beforeArray
      .map((item, idx) => ({ item, idx }))
      .filter(({ idx }) => !usedBeforeIndices.has(idx));
    const unmatchedAfter = afterArray
      .map((item, idx) => ({ item, idx }))
      .filter(({ idx }) => !usedAfterIndices.has(idx));

    const pairCount = Math.min(unmatchedBefore.length, unmatchedAfter.length);
    for (let i = 0; i < pairCount; i++) {
      usedBeforeIndices.add(unmatchedBefore[i].idx);
      usedAfterIndices.add(unmatchedAfter[i].idx);
      pushMatchedPair(unmatchedBefore[i].item, unmatchedAfter[i].item);
    }
  }

  // Remaining unmatched items (added or removed)
  for (let i = 0; i < beforeArray.length; i++) {
    if (usedBeforeIndices.has(i)) continue;
    filteredBefore.push(filterEmptyValues(beforeArray[i]));
  }
  for (let i = 0; i < afterArray.length; i++) {
    if (usedAfterIndices.has(i)) continue;
    filteredAfter.push(filterEmptyValues(afterArray[i]));
  }

  return {
    before: filteredBefore.length > 0 ? filteredBefore : detail.before,
    after: filteredAfter.length > 0 ? filteredAfter : detail.after,
  };
};
